import {
	assetList,
	directionList,
	durationList,
	patternSpecificationList,
  stageList,
} from '@here/api'
import dayjs from 'dayjs'
import * as v from 'valibot'

export const GeneralSchema = v.object({
	id: v.optional(v.string()),
	created_by: v.string(),
	duration: v.picklist(durationList),
	symbol: v.pipe(v.string(), v.nonEmpty('Please enter a symbol')),
	title: v.optional(v.string()),
	description: v.string(),
	market: v.picklist(assetList),
	stage: v.picklist(stageList),
	exchange: v.pipe(v.string(), v.nonEmpty('Please enter an exhange')),
	image_macro: v.pipe(
		v.string(),
		v.regex(
			/^https:\/\/([a-z0-9-]+\.)*tradingview\.com\/[^\s]+$/i,
			'The URL must be from tradingview.com',
		),
	),
	image_pattern: v.pipe(
		v.string(),
		v.regex(
			/^https:\/\/([a-z0-9-]+\.)*tradingview\.com\/[^\s]+$/i,
			'The URL must be from tradingview.com',
		),
	),
	image_trend: v.pipe(
		v.string(),
		v.regex(
			/^https:\/\/([a-z0-9-]+\.)*tradingview\.com\/[^\s]+$/i,
			'The URL must be from tradingview.com',
		),
	),
	image_trigger: v.pipe(
		v.string(),
		v.regex(
			/^https:\/\/([a-z0-9-]+\.)*tradingview\.com\/[^\s]+$/i,
			'The URL must be from tradingview.com',
		),
	),
	image_time_stop: v.optional(
		v.nullable(
			v.union([
				v.literal(''),
				v.pipe(
					v.string(),
					v.regex(
						/^https:\/\/([a-z0-9-]+\.)*tradingview\.com\/[^\s]+$/i,
						'The URL must be from tradingview.com',
					),
				),
			]),
		),
	),
	image_volume_by_price: v.optional(
		v.nullable(
			v.union([
				v.literal(''),
				v.pipe(
					v.string(),
					v.regex(
						/^https:\/\/([a-z0-9-]+\.)*tradingview\.com\/[^\s]+$/i,
						'The URL must be from tradingview.com',
					),
				),
			]),
		),
	),
	first_in_new_trend: v.optional(v.boolean()),
	has_primer: v.optional(v.boolean()),
	trigger_date: v.any(), // todo type this properly
})

export const TechnicalSchema = v.pipe(
	v.object({
		high_1_price: v.pipe(
			v.number(),
			v.custom<number>((input) => input !== 0),
		),
		high_1_time: v.optional(
			v.pipe(
				v.string(),
				v.custom<string>((input) => input !== '0'),
			),
		),
		high_2_price: v.pipe(
			v.number(),
			v.custom<number>((input) => input !== 0),
		),
		high_2_time: v.optional(
			v.pipe(
				v.string(),
				v.custom<string>((input) => input !== '0'),
			),
		),
		high_3_price: v.pipe(
			v.number(),
			v.custom<number>((input) => input !== 0),
		),
		high_3_time: v.optional(
			v.pipe(
				v.string(),
				v.custom<string>((input) => input !== '0'),
			),
		),
		low_1_price: v.pipe(
			v.number(),
			v.custom<number>((input) => input !== 0),
		),
		low_1_time: v.optional(
			v.pipe(
				v.string(),
				v.custom<string>((input) => input !== '0'),
			),
		),
		low_2_price: v.pipe(
			v.number(),
			v.custom<number>((input) => input !== 0),
		),
		low_2_time: v.optional(
			v.pipe(
				v.string(),
				v.custom<string>((input) => input !== '0'),
			),
		),
		low_3_price: v.pipe(
			v.number(),
			v.custom<number>((input) => input !== 0),
		),
		low_3_time: v.optional(
			v.pipe(
				v.string(),
				v.custom<string>((input) => input !== '0'),
			),
		),
	}),
	v.partialCheck(
		[['high_1_price'], ['high_2_price']],
		(input) => input.high_1_price > input.high_2_price,
		'HVF_ERROR: high_1_price must be greater than high_2_price',
	),
	v.partialCheck(
		[['high_2_price'], ['high_3_price']],
		(input) => input.high_2_price > input.high_3_price,
		'HVF_ERROR: high_2_price must be greater than high_3_price',
	),
	v.partialCheck(
		[['low_1_price'], ['low_2_price']],
		(input) => input.low_1_price < input.low_2_price,
		'HVF_ERROR: low_1_price must be less than low_2_price',
	),
	v.partialCheck(
		[['low_2_price'], ['low_3_price']],
		(input) => input.low_2_price < input.low_3_price,
		'HVF_ERROR: low_2_price must be less than than low_3_price',
	),
	v.partialCheck(
		[['high_3_price'], ['low_3_price']],
		(input) => input.high_3_price > input.low_3_price,
		'HVF_ERROR: high_3_price must be greater than than low_3_price',
	),
	v.partialCheck(
		[['high_1_time'], ['high_2_time']],
		(input) => validateTme(input.high_1_time, input.high_2_time),
		'HVF_ERROR: high_1_time must be before than high_2_time',
	),
	v.partialCheck(
		[['high_2_time'], ['high_3_time']],
		(input) => validateTme(input.high_2_time, input.high_3_time),
		'HVF_ERROR: high_2_time must be before than high_3_time',
	),
	v.partialCheck(
		[['low_1_time'], ['low_2_time']],
		(input) => validateTme(input.low_1_time, input.low_2_time),
		'HVF_ERROR: low_1_time must be after low_2_time',
	),
	v.partialCheck(
		[['low_2_time'], ['low_3_time']],
		(input) => validateTme(input.low_2_time, input.low_3_time),
		'HVF_ERROR: low_2_time must be after than low_3_time',
	),
)

const validateTme = (timeA?: string, timeB?: string) => {
	if (!timeA || !timeB) return true
	return dayjs(timeA).isBefore(dayjs(timeB))
}

export const ComputedSchema = v.object({
	position: v.picklist(directionList),
	pattern_specification: v.picklist(patternSpecificationList),
	risk_reward_ratio: v.number(),
	target: v.number(),
	interim_1: v.number(),
	interim_2: v.number(),
	axis: v.number(),
	time_stop: v.any(), // fill this in
})

export const CreateSetupSchema = v.object({
	...GeneralSchema.entries,
	...TechnicalSchema.entries,
	...ComputedSchema.entries,
})
